// src/theme/ThemeProvider.jsx

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { THEME_STORAGE_KEY, themeTokens } from "./tokens";

const ThemeContext = createContext(null);

const LEGACY_THEME_STORAGE_KEY = "neo-theme";

function normaliseTheme(value) {
  return value === "light" || value === "dark" ? value : null;
}

function getInitialMode() {
  try {
    const saved = normaliseTheme(localStorage.getItem(THEME_STORAGE_KEY));
    if (saved) return saved;

    // Older Neo builds saved the theme under `neo-theme`. Keep existing users
    // on their selected theme, but move everything to one shared key.
    const legacy = normaliseTheme(localStorage.getItem(LEGACY_THEME_STORAGE_KEY));
    if (legacy) {
      localStorage.setItem(THEME_STORAGE_KEY, legacy);
      return legacy;
    }
  } catch {}

  return "light";
}

function applyThemeToDom(mode) {
  const tokens = themeTokens[mode] || themeTokens.light;
  const root = document.documentElement;
  const body = document.body;

  root.setAttribute("data-theme", mode);
  body?.setAttribute("data-theme", mode);

  Object.entries(tokens).forEach(([key, value]) => {
    root.style.setProperty(`--neo-${key}`, String(value));
  });

  root.style.setProperty("color-scheme", tokens.colorScheme);
}

export function ThemeProvider({ children }) {
  const [themeMode, setThemeModeState] = useState(getInitialMode);

  useEffect(() => {
    applyThemeToDom(themeMode);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, themeMode);
      localStorage.setItem(LEGACY_THEME_STORAGE_KEY, themeMode);
    } catch {}
  }, [themeMode]);

  useEffect(() => {
    if (!window.matchMedia) return;

    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e) => {
      try {
        const saved = normaliseTheme(localStorage.getItem(THEME_STORAGE_KEY));
        if (saved) return;
      } catch {}
      setThemeModeState(e.matches ? "dark" : "light");
    };

    if (media.addEventListener) media.addEventListener("change", onChange);
    else media.addListener(onChange);

    return () => {
      if (media.removeEventListener) media.removeEventListener("change", onChange);
      else media.removeListener(onChange);
    };
  }, []);

  const setThemeMode = useCallback((mode) => {
    if (mode !== "light" && mode !== "dark") return;
    setThemeModeState(mode);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeModeState((prev) => (prev === "dark" ? "light" : "dark"));
  }, []);

  const value = useMemo(
    () => ({
      themeMode,
      resolvedMode: themeMode,
      tokens: themeTokens[themeMode] || themeTokens.light,
      setThemeMode,
      toggleTheme,
    }),
    [themeMode, setThemeMode, toggleTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used inside ThemeProvider");
  }
  return ctx;
}